import { useState } from "react";
import { AlertCircle, ExternalLink } from "lucide-react";
import type { CheckResponseData, PlatformDefinition, PlatformId, PlatformCheckResult } from "@handlequest/shared";
import { StatusBadge } from "./StatusBadge";
import { ShareActions } from "./ShareActions";

interface ResultGridProps {
  result: CheckResponseData | null;
  loading: boolean;
  selectedPlatforms: PlatformId[];
  platforms: PlatformDefinition[];
}

function statusFor(result: PlatformCheckResult): "available" | "taken" | "unknown" {
  if (result.available === true) return "available";
  if (result.available === false) return "taken";
  return "unknown";
}

export function ResultGrid({ result, loading, selectedPlatforms, platforms }: ResultGridProps) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (loading) {
    const loadingPlatforms = platforms.filter((platform) => selectedPlatforms.includes(platform.id));
    return (
      <section className="results-section" aria-label="Checking platforms">
        <div className="result-grid">
          {loadingPlatforms.map((platform) => (
            <article className="result-card visible skeleton" key={platform.id}>
              <div className="card-top">
                <span className="platform-title"><span className="platform-mark">{platform.shortName}</span><strong>{platform.name}</strong></span>
                <StatusBadge status="checking" />
              </div>
              <p className="result-meta">Opening availability channel...</p>
            </article>
          ))}
        </div>
      </section>
    );
  }

  if (!result) {
    return (
      <section className="empty-state">
        <AlertCircle aria-hidden="true" />
        <p>Enter a gamer tag to start the cross-platform scan.</p>
      </section>
    );
  }

  return (
    <section className="results-section" aria-label="Availability results">
      <div className="summary-bar">
        <div className="summary-title">
          <span>Current scan</span>
          <strong>{result.username}</strong>
        </div>
        <div className="summary-stats">
          <span className="stat-pill available">{result.summary.available} available</span>
          <span className="stat-pill taken">{result.summary.unavailable} taken</span>
          <span className="stat-pill unknown">{result.summary.unknown} review</span>
        </div>
        <ShareActions result={result} />
      </div>

      <div className="result-grid">
        {result.results.map((item, index) => {
          const isExpanded = expanded === item.platform;
          const platform = platforms.find((candidate) => candidate.id === item.platform);
          const platformMark = platform?.shortName ?? item.name.slice(0, 2).toUpperCase();
          return (
            <article
              className="result-card visible"
              style={{ animationDelay: `${index * 45}ms` }}
              key={item.platform}
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              onClick={() => setExpanded(isExpanded ? null : item.platform)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setExpanded(isExpanded ? null : item.platform);
                }
              }}
            >
              <span className="card-top">
                <span className="platform-title">
                  <span className="platform-mark">{platformMark}</span>
                  <span>
                    <strong>{item.name}</strong>
                    <small>{item.category}</small>
                  </span>
                </span>
                <StatusBadge status={statusFor(item)} />
              </span>
              <span className="handle-line">@{result.username}</span>
              <span className="result-meta">{item.cached ? "Cached result" : "Live result"} in {item.responseTime}ms</span>
              {isExpanded && (
                <span className="detail-panel">
                  {item.error || (item.available ? "No exact match found." : "Exact match found.")}
                  {item.profileUrl && (
                    <a href={item.profileUrl} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>
                      Open profile <ExternalLink aria-hidden="true" />
                    </a>
                  )}
                </span>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
